import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, Activity, ArrowRightLeft, AlertCircle, CheckCircle2, Clock, MoreVertical, Play, Pause } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell } from 'recharts';
import { Card, StatusPill, SystemBadge, Button } from '../components/ui/Primitives';
import { mockDashboardData, mockEvents } from '../components/data/mockData';
export function Dashboard() {
  const [activeSource, setActiveSource] = useState('All');
  const [feedPaused, setFeedPaused] = useState(false);
  const [liveEvents, setLiveEvents] = useState(mockEvents.slice(0, 10));
  useEffect(() => {
    if (feedPaused) return;
    const interval = setInterval(() => {
      const randomEvent = mockEvents[Math.floor(Math.random() * mockEvents.length)];
      const newEvent = {
        ...randomEvent,
        id: `evt_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString()
      };
      setLiveEvents(prev => [newEvent, ...prev].slice(0, 15));
    }, 3500);
    return () => clearInterval(interval);
  }, [feedPaused]);
  return <div className="space-y-6 max-w-[1600px] mx-auto" data-id="element-207">
      {/* Top KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-208">
        <KpiCard title="Total Synced Today" value={mockDashboardData.kpis.syncedToday.toLocaleString()} delta={mockDashboardData.kpis.syncedDelta} color="emerald" data-id="element-209" />
        <KpiCard title="Skipped Events" value={mockDashboardData.kpis.skipped.toLocaleString()} color="amber" data-id="element-210" />
        <KpiCard title="No Match Records" value={mockDashboardData.kpis.noMatch.toLocaleString()} color="slate" data-id="element-211" />
        <KpiCard title="Failed Syncs" value={mockDashboardData.kpis.failed.toLocaleString()} alert="+5 in last hour" color="red" data-id="element-212" />
        <KpiCard title="Active Queue Jobs" value={mockDashboardData.kpis.activeJobs.toLocaleString()} color="electric" pulse data-id="element-213" />
        <Card className="p-4 flex flex-col justify-between" data-id="element-214">
          <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase" data-id="element-215">
            API Health
          </div>
          <div className="flex items-center gap-2 mt-2" data-id="element-216">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-217" />
            <span className="text-xl font-semibold text-slate-800" data-id="element-218">
              Operational
            </span>
          </div>
          <div className="flex items-center gap-2 mt-3" data-id="element-219">
            <SystemBadge system="bonzo" showLabel={false} data-id="element-220" />
            <SystemBadge system="ghl" showLabel={false} data-id="element-221" />
            <SystemBadge system="five9" showLabel={false} data-id="element-222" />
            <SystemBadge system="salesforce" showLabel={false} data-id="element-223" />
            <span className="ml-auto text-xs font-mono text-slate-500" data-id="element-224">
              {mockDashboardData.kpis.latency}ms avg
            </span>
          </div>
        </Card>
      </div>

      {/* Second Row: Throughput & Systems Online */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-225">
        <Card className="lg:col-span-2 p-5 flex flex-col" data-id="element-226">
          <div className="flex items-center justify-between mb-6" data-id="element-227">
            <div className="flex items-center gap-2" data-id="element-228">
              <h2 className="text-sm font-semibold text-slate-800" data-id="element-229">
                Webhook Throughput
              </h2>
              <StatusPill status="processing" dot pulse className="bg-transparent border-none px-0" data-id="element-230" />
            </div>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg" data-id="element-231">
              {['All', 'Bonzo', 'GHL', 'Five9'].map(sys => <button key={sys} onClick={() => setActiveSource(sys)} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${sys === activeSource ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-232">
                  {sys}
                </button>)}
            </div>
          </div>
          <div className="h-[240px] w-full" data-id="element-233">
            <ResponsiveContainer width="100%" height="100%" data-id="element-234">
              <AreaChart data={mockDashboardData.throughput} margin={{
              top: 0,
              right: 0,
              left: -20,
              bottom: 0
            }} data-id="element-235">
                <defs data-id="element-236">
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-237">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} data-id="element-238" />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} data-id="element-239" />
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" hide data-id="element-240" />
                <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} axisLine={false} data-id="element-241" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} labelStyle={{
                color: '#64748B',
                fontSize: '12px'
              }} data-id="element-242" />
                <Area type="monotone" dataKey={activeSource === 'All' ? 'value' : activeSource.toLowerCase()} stroke="#3B82F6" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" data-id="element-243" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-id="element-244">
          <h2 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-245">
            Systems Online
          </h2>
          <div className="space-y-4" data-id="element-246">
            <SystemRow system="bonzo" status="Healthy" latency="124ms" uptime="99.9%" data-id="element-247" />
            <SystemRow system="ghl" status="Degraded" latency="842ms" uptime="99.5%" data-id="element-248" />
            <SystemRow system="five9" status="Healthy" latency="86ms" uptime="99.9%" data-id="element-249" />
            <SystemRow system="salesforce" status="Healthy" latency="210ms" uptime="100%" data-id="element-250" />
          </div>
        </Card>
      </div>

      {/* Third Row: Queue, Latency, Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-251">
        <Card className="p-5" data-id="element-252">
          <h2 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-253">
            Queue Depth
          </h2>
          <div className="space-y-4" data-id="element-254">
            <QueueBar label="Inbound Bonzo" value={124} max={500} color="bg-orange-500" data-id="element-255" />
            <QueueBar label="Inbound GHL" value={842} max={1000} color="bg-teal-500" data-id="element-256" />
            <QueueBar label="Outbound Five9" value={12} max={100} color="bg-violet-500" data-id="element-257" />
            <QueueBar label="SF Platform Events" value={0} max={100} color="bg-sky-500" data-id="element-258" />
          </div>
        </Card>

        <Card className="p-5" data-id="element-259">
          <h2 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-260">
            API Latency (ms)
          </h2>
          <div className="h-[180px] w-full mt-2" data-id="element-261">
            <ResponsiveContainer width="100%" height="100%" data-id="element-262">
              <LineChart data={mockDashboardData.latency} margin={{
              top: 5,
              right: 5,
              left: -20,
              bottom: 0
            }} data-id="element-263">
                <XAxis dataKey="time" hide data-id="element-264" />
                <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} axisLine={false} data-id="element-265" />
                <Tooltip contentStyle={{
                fontSize: '12px',
                borderRadius: '8px'
              }} data-id="element-266" />
                <Line type="monotone" dataKey="bonzo" stroke="#f97316" strokeWidth={2} dot={false} data-id="element-267" />
                <Line type="monotone" dataKey="ghl" stroke="#14b8a6" strokeWidth={2} dot={false} data-id="element-268" />
                <Line type="monotone" dataKey="five9" stroke="#8b5cf6" strokeWidth={2} dot={false} data-id="element-269" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-id="element-270">
          <div className="flex items-center justify-between mb-4" data-id="element-271">
            <h2 className="text-sm font-semibold text-slate-800" data-id="element-272">
              Top Failure Reasons
            </h2>
          </div>
          <div className="space-y-3" data-id="element-273">
            {mockDashboardData.failureReasons.slice(0, 4).map((reason, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-274">
                <div className="flex items-center gap-2 flex-1" data-id="element-275">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-276" />
                  <span className="text-slate-600 truncate" data-id="element-277">
                    {reason.reason}
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-278">
                  <span className="text-slate-400 text-xs w-8 text-right" data-id="element-279">
                    {reason.percentage}%
                  </span>
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-280">
                    <div className="h-full bg-red-400 rounded-full" style={{
                  width: `${reason.percentage}%`
                }} data-id="element-281" />
                  </div>
                </div>
              </div>)}
          </div>
        </Card>
      </div>

      {/* Fourth Row: Activity Feed */}
      <Card className="p-0 overflow-hidden flex flex-col" data-id="element-282">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-surface" data-id="element-283">
          <div className="flex items-center gap-2" data-id="element-284">
            <Activity className="w-4 h-4 text-electric-500" data-id="element-285" />
            <h2 className="text-sm font-semibold text-slate-800" data-id="element-286">
              Real-time Sync Activity
            </h2>
          </div>
          <div className="flex items-center gap-2" data-id="element-287">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setFeedPaused(!feedPaused)} data-id="element-288">
              {feedPaused ? <>
                  <Play className="w-3 h-3 mr-1" data-id="element-289" /> Resume
                </> : <>
                  <Pause className="w-3 h-3 mr-1" data-id="element-290" /> Pause
                </>}
            </Button>
          </div>
        </div>
        <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto" data-id="element-291">
          {liveEvents.map((event, i) => <motion.div key={event.id} initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05
        }} className="flex items-center gap-4 p-3 hover:bg-slate-50 transition-colors group relative" data-id="element-292">
              {i === 0 && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-electric-500 animate-pulse" data-id="element-293" />}
              <div className="w-20 text-xs font-mono text-slate-400 shrink-0" data-id="element-294">
                {new Date(event.timestamp).toLocaleTimeString([], {
              hour12: false
            })}
              </div>
              <div className="flex items-center gap-2 w-32 shrink-0" data-id="element-295">
                <SystemBadge system={event.source as any} showLabel={false} data-id="element-296" />
                <ArrowRightLeft className="w-3 h-3 text-slate-300" data-id="element-297" />
                <SystemBadge system={event.target as any} showLabel={false} data-id="element-298" />
              </div>
              <div className="w-32 text-sm font-mono text-slate-700 shrink-0" data-id="element-299">
                {event.phone}
              </div>
              <div className="w-32 text-sm text-slate-600 truncate shrink-0" data-id="element-300">
                {event.type}
              </div>
              <div className="flex-1 flex justify-end" data-id="element-301">
                <StatusPill status={event.status as any} data-id="element-302" />
              </div>
              <button className="p-1 text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-303">
                <MoreVertical className="w-4 h-4" data-id="element-304" />
              </button>
            </motion.div>)}
        </div>
      </Card>
    </div>;
}
function KpiCard({
  title,
  value,
  delta,
  alert,
  color,
  pulse
}: any) {
  const colors = {
    emerald: 'text-emerald-500',
    amber: 'text-amber-500',
    red: 'text-red-500',
    slate: 'text-slate-500',
    electric: 'text-electric-500'
  };
  return <Card className="p-4 flex flex-col justify-between relative overflow-hidden group" data-id="element-305">
      <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase z-10" data-id="element-306">
        {title}
      </div>
      <div className="mt-2 flex items-baseline gap-2 z-10" data-id="element-307">
        <span className="text-2xl font-semibold text-slate-800 tabular-nums tracking-tight" data-id="element-308">
          {value}
        </span>
        {pulse && <span className="w-2 h-2 rounded-full bg-electric-500 animate-pulse mb-1" data-id="element-309" />}
      </div>
      <div className="mt-2 h-4 flex items-center z-10" data-id="element-310">
        {delta && <span className={`flex items-center text-xs font-medium ${delta > 0 ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-311">
            {delta > 0 ? <ArrowUpRight className="w-3 h-3 mr-0.5" data-id="element-312" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" data-id="element-313" />}
            {Math.abs(delta)}%
          </span>}
        {alert && <span className="text-xs font-medium text-red-500" data-id="element-314">{alert}</span>}
      </div>
      {/* Decorative background element */}
      <div className={`absolute -right-4 -bottom-4 w-16 h-16 rounded-full opacity-5 group-hover:scale-150 transition-transform duration-500 ${colors[color as keyof typeof colors].replace('text-', 'bg-')}`} data-id="element-315" />
    </Card>;
}
function SystemRow({
  system,
  status,
  latency,
  uptime
}: any) {
  const isHealthy = status === 'Healthy';
  return <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-316">
      <div className="flex items-center gap-3" data-id="element-317">
        <SystemBadge system={system} showLabel={false} data-id="element-318" />
        <span className="text-sm font-medium text-slate-700 capitalize" data-id="element-319">
          {system}
        </span>
      </div>
      <div className="flex items-center gap-4" data-id="element-320">
        <div className="flex flex-col items-end" data-id="element-321">
          <span className="text-xs font-mono text-slate-500" data-id="element-322">{latency}</span>
          <span className="text-[10px] text-slate-400" data-id="element-323">{uptime}</span>
        </div>
        <StatusPill status={isHealthy ? 'success' : 'warning'} dot pulse={isHealthy} className="w-24 justify-center" data-id="element-324" />
      </div>
    </div>;
}
function QueueBar({
  label,
  value,
  max,
  color
}: any) {
  const percent = Math.min(100, value / max * 100);
  return <div data-id="element-325">
      <div className="flex justify-between text-xs mb-1.5" data-id="element-326">
        <span className="font-medium text-slate-600" data-id="element-327">{label}</span>
        <span className="font-mono text-slate-500" data-id="element-328">{value}</span>
      </div>
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-329">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${percent}%`
      }} className={`h-full rounded-full ${color}`} data-id="element-330" />
      </div>
    </div>;
}